import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useWorkoutStore } from '../stores/workoutStore';
import { useDebounce } from '../hooks/useDebounce';
import type { Exercise } from '../types/workout';

interface ExerciseEditorProps {
  date: string;
  sessionIndex: number;
  exercise: Exercise;
  exerciseIndex: number;
}

export function ExerciseEditor({
  date,
  sessionIndex,
  exercise,
  exerciseIndex,
}: ExerciseEditorProps) {
  const { updateExercise, deleteExercise, isSaving } = useWorkoutStore();

  const [localExercise, setLocalExercise] = useState<Exercise>(exercise);

  const debouncedExercise = useDebounce(localExercise, 500);

  useEffect(() => {
    setLocalExercise(exercise);
  }, [exercise]);

  // Auto-save when debounced value changes. We include `date`,
  // `sessionIndex`, and `exerciseIndex` in the deps so a re-keyed parent
  // (e.g. session reorder) cannot leak a stale closure into the save.
  useEffect(() => {
    if (
      debouncedExercise.exercise === exercise.exercise &&
      debouncedExercise.sets === exercise.sets &&
      debouncedExercise.reps === exercise.reps &&
      debouncedExercise.weight === exercise.weight
    ) {
      return;
    }
    updateExercise(date, sessionIndex, exerciseIndex, debouncedExercise);
  }, [debouncedExercise, date, sessionIndex, exerciseIndex, exercise, updateExercise]);

  const handleFieldChange = (field: keyof Exercise, value: string | number) => {
    setLocalExercise((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = () => {
    if (confirm(`Delete "${exercise.exercise}"?`)) {
      deleteExercise(date, sessionIndex, exerciseIndex);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-2.5 group hover:border-gray-300 transition-colors">
      {/* Exercise Name — `min-w-0` lets the flex child shrink below its
          intrinsic content width so long names like "Bulgarian Split Squat"
          compress instead of pushing the delete button off the column. */}
      <div className="flex items-start gap-1 mb-2">
        <input
          type="text"
          value={localExercise.exercise}
          onChange={(e) => handleFieldChange('exercise', e.target.value)}
          className="min-w-0 flex-1 font-medium text-sm text-gray-900 bg-transparent border-none focus:outline-hidden focus:ring-2 focus:ring-primary rounded px-1 py-1 truncate"
          placeholder="Exercise name"
        />

        {/* Delete Button — always visible (was opacity-0 → group-hover) so
            the admin can find it without hover-discovery. shrink-0 keeps it
            from being squeezed when the column is narrow. */}
        <button
          onClick={handleDelete}
          className="shrink-0 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
          title="Delete exercise"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Sets, Reps, Weight — tighter input widths so the row fits on one
          line at the new column width. flex-wrap is the fallback. */}
      <div className="flex items-center flex-wrap gap-1.5 text-xs">
        <div className="flex items-center gap-1">
          <label className="text-gray-600">Sets</label>
          <input
            type="number"
            value={localExercise.sets}
            onChange={(e) => handleFieldChange('sets', parseInt(e.target.value) || 0)}
            className="w-11 px-1.5 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary tabular-nums"
            min="1"
          />
        </div>

        <div className="flex items-center gap-1">
          <label className="text-gray-600">Reps</label>
          <input
            type="text"
            value={localExercise.reps}
            onChange={(e) => handleFieldChange('reps', e.target.value)}
            className="w-14 px-1.5 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary"
            placeholder="8-10"
          />
        </div>

        <div className="flex items-center gap-1">
          <label className="text-gray-600">Wt</label>
          <input
            type="number"
            value={localExercise.weight}
            onChange={(e) => handleFieldChange('weight', parseFloat(e.target.value) || 0)}
            className="w-12 px-1.5 py-1 border border-gray-300 rounded focus:ring-2 focus:ring-primary focus:border-primary tabular-nums"
            min="0"
            step="2.5"
          />
          <span className="text-gray-500">lb</span>
        </div>

        {isSaving && (
          <div className="ml-auto flex items-center gap-1 text-gray-500">
            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500"></div>
          </div>
        )}
      </div>
    </div>
  );
}
